import { useState, useEffect, useRef } from "react";
import API from "../../services/api";
import AdminLayout from "../../layouts/AdminLayout";
import { Key, CheckCircle2, PauseCircle, XCircle, Trash2, AlertTriangle, RefreshCw, Plus, Search, X } from "lucide-react";

// ─── AP DESIGN TOKENS (matching ProjectList.js exactly) ──────────────────────
const T = {
  violet:      "#5C2D91",
  violetMid:   "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red:         "#C8102E",
  redLight:    "#FCEAED",
  orange:      "#E8640A",
  orangeLight: "#FEF0E6",
  gold:        "#C87800",
  goldLight:   "#FEF5DC",
  green:       "#1A7A42",
  greenLight:  "#E2F5EC",
  bg:          "#F4F0FA",
  surface:     "#FFFFFF",
  surfaceAlt:  "#FAF8FD",
  dark:        "#1A0A2E",
  muted:       "#6B5A8A",
  faint:       "#C4B8DC",
  border:      "rgba(92,45,145,0.1)",
  borderSolid: "#E8E0F5",
};

/* ─── Role colour config (UNCHANGED) ───────────────────────────────────────── */
const roleConfig = {
  Admin:    { color: T.red,    bg: T.redLight,    grad: `linear-gradient(135deg,${T.red},#E02E49)`,             desc: "Full system access"      },
  Manager:  { color: T.violet, bg: T.violetLight, grad: `linear-gradient(135deg,${T.violet},${T.violetMid})`,  desc: "Manage projects & teams" },
  Engineer: { color: T.green,  bg: T.greenLight,  grad: `linear-gradient(135deg,${T.green},#22A458)`,          desc: "Update project progress" },
  Client:   { color: T.gold,   bg: T.goldLight,   grad: `linear-gradient(135deg,${T.gold},#E69A0B)`,           desc: "View reports & updates"  },
};

const getRoleCfg = (title) =>
  roleConfig[title] || {
    color: T.violetMid,
    bg:    T.violetLight,
    grad:  `linear-gradient(135deg,${T.violet},${T.violetMid})`,
    desc:  "Custom role",
  };

/* ─── Initials avatar (UNCHANGED logic, AP colors) ─────────────────────────── */
function RoleAvatar({ title, size = 40 }) {
  const cfg      = getRoleCfg(title);
  const initials = (title || "??").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: cfg.grad,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 800, fontSize: size * 0.38, color: "#fff",
      flexShrink: 0, letterSpacing: "0.04em", userSelect: "none",
      boxShadow: `0 2px 8px ${cfg.color}40`,
    }}>
      {initials}
    </div>
  );
}

/* ─── Modal component (from DiscussionTopicMaster) ──────────────────────────── */
function Modal({ modal, onClose }) {
  if (!modal) return null;
  const isConfirm = modal.type === "confirm";
  const isError   = modal.type === "error";
 const iconMap   = { success: <CheckCircle2 size={22} />, error: <XCircle size={22} />, confirm: <Trash2 size={22} />, validation: <AlertTriangle size={22} /> };
  const colorMap  = {
    success:    { accent: T.green,  light: T.greenLight,  border: "#A7F3D0" },
    error:      { accent: T.red,    light: T.redLight,    border: "#FECDD3" },
    confirm:    { accent: T.red,    light: T.redLight,    border: "#FECDD3" },
    validation: { accent: T.orange, light: T.orangeLight, border: "#FED7AA" },
  };
  const c = colorMap[modal.type] || colorMap.success;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(26,10,46,0.45)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "rm-modal-fadein 0.18s ease",
    }}>
      <style>{`
        @keyframes rm-modal-fadein { from { opacity:0; transform:scale(0.94); } to { opacity:1; transform:scale(1); } }
      `}</style>
      <div style={{
        background: T.surface, borderRadius: 20, width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(92,45,145,0.22), 0 4px 16px rgba(0,0,0,0.10)",
        border: `1px solid ${T.borderSolid}`, overflow: "hidden",
      }}>
        {/* Accent top bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${c.accent})` }} />
        <div style={{ padding: "28px 28px 24px" }}>
          {/* Icon + title */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: c.light, border: `1.5px solid ${c.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>{iconMap[modal.type]}</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: T.dark, lineHeight: 1.25 }}>{modal.title}</div>
              {modal.subtitle && (
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{modal.subtitle}</div>
              )}
            </div>
          </div>
          {/* Message */}
          <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.65, margin: "0 0 24px", fontWeight: 400 }}>
            {modal.message}
          </p>
          {/* Buttons */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            {isConfirm && (
              <button
                onClick={onClose}
                style={{
                  padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 600,
                  background: T.surfaceAlt, color: T.muted,
                  border: `1.5px solid ${T.borderSolid}`, cursor: "pointer",
                }}
              >Cancel</button>
            )}
            <button
              onClick={() => { if (modal.onConfirm) modal.onConfirm(); onClose(); }}
              style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 700,
                background: isConfirm || isError
                  ? `linear-gradient(135deg,${T.red},#E02040)`
                  : `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none", cursor: "pointer",
                boxShadow: `0 4px 14px ${isConfirm || isError ? T.red : T.violet}40`,
              }}
            >{isConfirm ? "Delete" : "OK"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function RoleMaster() {

  /* ── state — ALL UNCHANGED ── */
  const [roles,    setRoles]    = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [search,   setSearch]   = useState("");
  const [editId,   setEditId]   = useState(null);
  const [hov,      setHov]      = useState(null);
  const [form,     setForm]     = useState({ title: "", status: "Active" });

  /* ── modal state — replaces deleteModal + toast with unified Modal pattern ── */
  const [modal, setModal] = useState(null);
 const [page, setPage] = useState(1);
const PAGE_SIZE = 10;
const formRef = useRef(null);
const [refreshing, setRefreshing] = useState(false);

  const showModal = (type, title, message, onConfirm = null, subtitle = "") =>
    setModal({ type, title, message, onConfirm, subtitle });
  const closeModal = () => setModal(null);

  /* ── fetch — UNCHANGED ── */
 // CHANGE TO:
const fetchRoles = async () => {
  try {
    const token = localStorage.getItem("token");
    const res = await API.get("/admin/roles", {
      headers: {
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-cache",
        "Pragma": "no-cache",
      },
      params: { _t: Date.now() },
    });
    const data = res.data?.data || res.data || [];
    setRoles([]);
    setTimeout(() => {
      setRoles(Array.isArray(data) ? data : []);
    }, 50);
  } catch (err) {
    console.error("Error connecting with dynamic role tables:", err);
  }
};
  useEffect(() => { fetchRoles(); }, []);
  useEffect(() => { setPage(1); }, [search]);

  /* ── save — UNCHANGED logic, toast replaced with Modal ── */
const saveRole = async () => {

  const title = form.title.trim();

  // Required
  if (!title) {
    showModal("validation", "Validation Error", "Role name is required.");
    return;
  }

  // Only letters
  if (!/^[A-Za-z ]+$/.test(title)) {
    showModal(
      "validation",
      "Validation Error",
      "Role name can contain only letters and spaces."
    );
    return;
  }

  // Minimum Length
  if (title.length < 3) {
    showModal(
      "validation",
      "Validation Error",
      "Role name must be at least 3 characters."
    );
    return;
  }

  // Maximum Length
  if (title.length > 50) {
    showModal(
      "validation",
      "Validation Error",
      "Role name cannot exceed 50 characters."
    );
    return;
  }

  
    try {
      const token  = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editId) {
        const res = await API.put(`/admin/roles/${editId}`, { title: title, status: form.status }, config);
        if (res.data.success) showModal("success", "Role Updated", "Role updated successfully.");
      } else {
        const res = await API.post("/admin/roles", { title: title, status: form.status }, config);
        if (res.data.success) showModal("success", "Role Added", "New role added successfully.");
      }
      setForm({ title: "", status: "Active" });
      setEditId(null);
      setShowForm(false);
      fetchRoles();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to finalize database role write operation.");
    }
  };

 /* ── edit ── */
const editRole = (r) => {
  setForm({ title: r.title, status: r.status });
  setEditId(r.id);
  setShowForm(true);
  setTimeout(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 100);
};

  /* ── delete — now uses Modal confirm pattern (from DiscussionTopicMaster) ── */
  const deleteRole = (id) => {
    showModal(
      "confirm",
      "Delete Role",
      "Are you sure you want to delete this role? This action cannot be undone.",
      async () => {
        try {
          const token = localStorage.getItem("token");
          const res   = await API.delete(`/admin/roles/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.success) {
            fetchRoles();
            showModal("success", "Role Deleted", "Role has been deleted successfully.");
          }
        } catch {
          showModal("error", "Delete Failed", "Failed dropping role structure tracking snapshot.");
        }
      }
    );
  };

 /* ── derived values — UNCHANGED ── */
const filtered      = roles.filter((r) => (r.title || "").toLowerCase().includes(search.toLowerCase()));
const activeCount   = roles.filter((r) => r.status === "Active").length;
const inactiveCount = roles.filter((r) => r.status === "Inactive").length;

/* ── pagination derived values ── */
const totalPages   = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
const safePage     = Math.min(page, totalPages);
const startIdx     = (safePage - 1) * PAGE_SIZE;
const paginated    = filtered.slice(startIdx, startIdx + PAGE_SIZE);
  /* ════════════════════════════════════════════════════════════════════════ */
  return (
    <AdminLayout>
      {/* ── Modal (replaces deleteModal + toast) ── */}
      <Modal modal={modal} onClose={closeModal} />

      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

       .rm-root * { font-family: 'Montserrat', sans-serif !important; }

        .rm-row:hover { background: ${T.violetFaint} !important; }

        .rm-search:focus-within {
          border-color: ${T.violet} !important;
          box-shadow: 0 0 0 3px rgba(92,45,145,0.1) !important;
        }

        .rm-add-btn:hover {
          box-shadow: 0 6px 20px rgba(92,45,145,0.45) !important;
          transform: translateY(-1px);
        }
        .rm-add-btn { transition: all 0.18s ease !important; }

       .rm-refresh:hover { opacity: 0.85; }
        .rm-refresh { transition: opacity 0.15s; cursor: pointer; }
        @keyframes rm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 1024px) {
          .rm-kpi-strip { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 768px) {
          .rm-header    { flex-direction: column !important; gap: 14px !important; }
          .rm-kpi-strip { grid-template-columns: repeat(2,1fr) !important; }
          .rm-filter-row{ flex-direction: column !important; align-items: flex-start !important; }
          .rm-table-wrap{ font-size: 12px !important; }
        }
        @media (max-width: 480px) {
          .rm-kpi-strip { grid-template-columns: 1fr 1fr !important; }
          .rm-page-pad  { padding: 14px 12px !important; }
        }
      `}</style>

      <div className="rm-root rm-page-pad" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ── PAGE HEADER ── */}
        <div className="rm-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.violet},${T.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · Masters
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
                Role Master
              </h1>
              <span style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", fontSize: 9, fontWeight: 700,
                padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                textTransform: "uppercase", boxShadow: `0 2px 8px ${T.violet}40`,
              }}>ADMIN</span>
            </div>
            <p style={{ color: T.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              Manage system roles and access levels
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
{/* Refresh button */}
<button
  className="rm-refresh"
  onClick={async () => {
    setRefreshing(true);
    const minSpin = new Promise(resolve => setTimeout(resolve, 700));
    await Promise.all([fetchRoles(), minSpin]);
    setRefreshing(false);
  }}
  disabled={refreshing}
  style={{
    width: 40, height: 40, borderRadius: 11,
    border: `1.5px solid ${T.borderSolid}`,
    background: T.surface, color: T.muted,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16, flexShrink: 0,
    cursor: refreshing ? "not-allowed" : "pointer",
    opacity: refreshing ? 0.7 : 1,
  }}
  title="Refresh"
>
  <span style={{
    display: "inline-flex",
    animation: refreshing ? "rm-spin 0.7s linear infinite" : "none",
  }}><RefreshCw size={16} /></span>
</button>

            {/* Add Role button */}
            <button
              className="rm-add-btn"
              onClick={() => {
                setShowForm((s) => !s);
                setEditId(null);
                setForm({ title: "", status: "Active" });
              }}
              style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none",
                padding: "11px 22px", borderRadius: 12,
                fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: `0 4px 14px ${T.violet}40`,
                whiteSpace: "nowrap", cursor: "pointer",
              }}
            >
              <Plus size={16} />
              {editId ? "Edit Role" : showForm ? "Close Form" : "Add Role"}
            </button>
          </div>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="rm-kpi-strip" style={{
          display: "grid", gridTemplateColumns: "repeat(3,1fr)",
          gap: 16, marginBottom: 24,
        }}>
          {[
{ label: "Total Roles", value: roles.length,  icon: <Key size={22} />, color: T.violet, bg: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: "#D8C8F5" },
            { label: "Active",      value: activeCount,   icon: <CheckCircle2 size={22} />, color: T.green,  bg: `linear-gradient(135deg,${T.greenLight},#EAFAF0)`,  border: "#B0E8C8" },
            { label: "Inactive",    value: inactiveCount, icon: <PauseCircle size={22} />,  color: T.red,    bg: `linear-gradient(135deg,${T.redLight},#FFF0F2)`,    border: "#F8C8D0" },
          ].map((s) => (
            <div key={s.label} style={{
              background: T.surface,
              borderRadius: 16,
              border: `1px solid ${T.borderSolid}`,
              padding: "20px 22px",
              display: "flex", alignItems: "center", gap: 14,
              boxShadow: "0 2px 12px rgba(92,45,145,0.06)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{
                position: "absolute", right: -10, top: -10,
                width: 70, height: 70, borderRadius: "50%",
                background: s.bg, opacity: 0.6,
              }} />
              <div style={{
                width: 46, height: 46, borderRadius: 13,
                background: s.bg,
                border: `1.5px solid ${s.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0, position: "relative",
              }}>{s.icon}</div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {s.label}
                </div>
                <div style={{ fontWeight: 800, fontSize: 28, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </div>
              </div>
            </div>
          ))}
        </div>

      {/* ── FORM CARD ── */}
{showForm && (
  <div ref={formRef} style={{
    background: T.surface, borderRadius: 20,
    border: `1px solid ${T.borderSolid}`, overflow: "hidden",
    marginBottom: 24, boxShadow: "0 4px 24px rgba(92,45,145,0.08)",
    scrollMarginTop: 100,
  }}>
            <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />
            <div style={{ padding: "26px 28px" }}>

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: T.violetLight,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
}}><Key size={17} /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>
                    {editId ? "Edit Role" : "Create New Role"}
                  </div>
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>Fill in the details below</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 20, marginBottom: 22 }}>
                {/* Role Title */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    Role Title
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Supervisor"
                    style={{
                      width: "100%", borderRadius: 12,
                      border: `1.5px solid ${T.borderSolid}`,
                      padding: "11px 14px", fontSize: 14, color: T.dark,
                      background: T.surfaceAlt, outline: "none",
                      boxSizing: "border-box", transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => e.target.style.borderColor = T.violet}
                    onBlur={(e)  => e.target.style.borderColor = T.borderSolid}
                  />
                </div>

                {/* Status toggle */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    Status
                  </label>
                  <div style={{ display: "flex", gap: 10 }}>
                  {["Active", "Inactive"].map((s) => (
  <button
    key={s}
    onClick={() => setForm((f) => ({ ...f, status: s }))}
    style={{
      flex: 1, padding: "11px 0", borderRadius: 12,
      border: `1.5px solid ${form.status === s ? (s === "Active" ? T.green : T.red) : T.borderSolid}`,
      background: form.status === s ? (s === "Active" ? T.greenLight : T.redLight) : T.surfaceAlt,
      color: form.status === s ? (s === "Active" ? T.green : T.red) : T.muted,
      fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.15s",
    }}
  >
    {s === "Active"
      ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><CheckCircle2 size={14} /> Active</span>
      : <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><PauseCircle size={14} /> Inactive</span>}
  </button>
))}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={saveRole}
                  style={{
                    background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                    color: "#fff", border: "none", padding: "11px 28px",
                    borderRadius: 12, fontSize: 14, fontWeight: 700,
                    cursor: "pointer", boxShadow: `0 4px 14px ${T.violet}40`, transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  {editId ? "Update Role" : "Save Role"}
                </button>
                <button
                  onClick={() => { setShowForm(false); setEditId(null); }}
                  style={{
                    background: T.surfaceAlt, color: T.muted,
                    border: `1.5px solid ${T.borderSolid}`,
                    padding: "11px 20px", borderRadius: 12,
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                  }}
                >Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── MAIN TABLE CARD ── */}
        <div style={{
          background: T.surface, borderRadius: 20,
          border: `1px solid ${T.borderSolid}`, overflow: "hidden",
          boxShadow: "0 4px 24px rgba(92,45,145,0.08)",
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />

          <div style={{ padding: "20px 24px" }}>

            {/* ── TABLE TOOLBAR ── */}
            <div className="rm-filter-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>All Roles</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 400 }}>
                  {filtered.length} of {roles.length} roles configured
                  {search && <span style={{ color: T.violet, fontWeight: 600 }}> · filtered</span>}
                </div>
              </div>

              <div className="rm-search" style={{
                display: "flex", alignItems: "center", gap: 8,
                background: T.surfaceAlt,
                border: `1.5px solid ${T.borderSolid}`,
                borderRadius: 12, padding: "9px 14px",
                minWidth: 240, transition: "all 0.2s",
              }}>
               <span style={{ color: T.faint, display: "inline-flex" }}><Search size={15} /></span>
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    border: "none", background: "transparent",
                    fontSize: 13, color: T.dark, outline: "none",
                    width: "100%", fontWeight: 400,
                  }}
                />
                {search && (
<button onClick={() => setSearch("")} style={{
                    background: T.violetLight, border: "none", color: T.violet,
                    cursor: "pointer", width: 18, height: 18,
                    borderRadius: "50%", display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0,
                  }}><X size={11} /></button>
                )}
              </div>
            </div>

            {/* ── TABLE ── */}
            <div className="rm-table-wrap" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 540 }}>
                <thead>
                  <tr style={{ background: T.violetFaint }}>
                    {[
                      { label: "#",       center: true  },
                      { label: "Role",    center: false },
                      { label: "Status",  center: false },
                      { label: "Created", center: false },
                      { label: "Actions", center: false },
                    ].map((h) => (
                      <th key={h.label} style={{
                        fontSize: 10, fontWeight: 700, color: T.muted,
                        textTransform: "uppercase", letterSpacing: "0.09em",
                        padding: "12px 16px",
                        textAlign: h.center ? "center" : "left",
                        borderBottom: `2px solid ${T.borderSolid}`,
                        borderTop: `1px solid ${T.borderSolid}`,
                        whiteSpace: "nowrap",
                        background: T.violetFaint,
                      }}>{h.label}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ padding: "52px", textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                          <div style={{
                            width: 64, height: 64, borderRadius: 18,
                            background: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`,
                            border: `2px dashed ${T.faint}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}><Search size={28} color={T.violet} /></div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: T.dark }}>No roles found</div>
                          <div style={{ fontSize: 13, color: T.muted }}>
                            {search ? `No results for "${search}".` : "No roles configured yet."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                   paginated.map((role, i) => {
                      const cfg      = getRoleCfg(role.title);
                      const isActive = role.status === "Active";
                      const isLast   = i === filtered.length - 1;

                      return (
                        <tr
                          key={role.id}
                          className="rm-row"
                          onMouseEnter={() => setHov(role.id)}
                          onMouseLeave={() => setHov(null)}
                          style={{ background: T.surface, transition: "background 0.12s" }}
                        >
                         {/* # */}
<td style={{ padding: "15px 16px", textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 26, height: 26, borderRadius: 8,
    background: T.violetFaint,
    fontWeight: 700, fontSize: 11, color: T.muted,
  }}>{startIdx + i + 1}</span>
</td>

                         {/* Role — name only */}
<td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
  <div style={{ fontSize: 14, fontWeight: 700, color: T.dark }}>
    {role.title}
  </div>
</td>

                          {/* Status badge */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              background: isActive ? T.greenLight : T.redLight,
                              color: isActive ? T.green : T.red,
                              fontSize: 11, fontWeight: 700,
                              padding: "5px 12px", borderRadius: 20,
                              border: `1px solid ${isActive ? "#B0E8C8" : "#F8C8D0"}`,
                              whiteSpace: "nowrap",
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />
                              {role.status}
                            </span>
                          </td>

                          {/* Created date — UNCHANGED data binding */}
                          <td style={{ padding: "15px 16px", fontSize: 13, color: T.muted, whiteSpace: "nowrap", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            {role.createdAt || "—"}
                          </td>

                          {/* Actions */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                onClick={() => editRole(role)}
                                style={{
                                  background: T.violetLight, color: T.violet,
                                  border: `1px solid ${T.borderSolid}`,
                                  padding: "7px 16px", borderRadius: 10,
                                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                                  transition: "all 0.15s", whiteSpace: "nowrap",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.violet;      e.currentTarget.style.color = "#fff"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = T.violetLight; e.currentTarget.style.color = T.violet; }}
                             ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg></button>
                              <button
                                onClick={() => deleteRole(role.id)}
                                style={{
                                  background: T.redLight, color: T.red,
                                  border: `1px solid #F8C8D0`,
                                  padding: "7px 16px", borderRadius: 10,
                                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                                  transition: "all 0.15s", whiteSpace: "nowrap",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.red;      e.currentTarget.style.color = "#fff"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = T.redLight; e.currentTarget.style.color = T.red; }}
                             ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
{/* ── TABLE FOOTER ── */}
{filtered.length > 0 && (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.borderSolid}`, flexWrap: "wrap", gap: 12 }}>
    <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
      Showing <strong style={{ color: T.dark }}>{Math.min((safePage - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(safePage * PAGE_SIZE, filtered.length)}</strong> of <strong style={{ color: T.dark }}>{filtered.length}</strong> roles
    </span>
    <div style={{ display: "flex", gap: 6 }}>
      <button
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={safePage === 1}
        style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${safePage === 1 ? T.borderSolid : T.violet}`, background: safePage === 1 ? T.surface : T.violetLight, color: safePage === 1 ? T.faint : T.violet, cursor: safePage === 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}
      >‹ Prev</button>
      {Array.from({ length: totalPages }, (_, i) => i + 1)
        .slice(Math.max(0, safePage - 3), Math.min(totalPages, safePage + 2))
        .map((n) => (
          <button key={n} onClick={() => setPage(n)}
            style={{ width: 34, height: 34, borderRadius: 10, border: `1.5px solid ${n === safePage ? T.violet : T.borderSolid}`, background: n === safePage ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface, color: n === safePage ? "#fff" : T.faint, fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: n === safePage ? `0 2px 8px ${T.violet}40` : "none" }}
          >{n}</button>
        ))}
      <button
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={safePage === totalPages}
        style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${safePage === totalPages ? T.borderSolid : T.violet}`, background: safePage === totalPages ? T.surface : T.violetLight, color: safePage === totalPages ? T.faint : T.violet, cursor: safePage === totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}
      >Next ›</button>
    </div>
  </div>
)}

          </div>
        </div>
      </div>
    </AdminLayout>
  );
}